/**
 * 开始节点组件
 *
 * 工作流的起点，仅右侧输出连接点
 * 显示任务 prompt 摘要
 */

import React, { memo } from 'react';
import type { NodeProps } from '@xyflow/react';
import type { StartNodeData } from '../../../types/workflow';
import { OutputHandle } from './CommonHandle';
import { useNodeExecutionStatus } from '../../../hooks/useNodeExecutionStatus';
import {
  cn,
  getNodeContainerClasses,
  getNodeHeaderClasses,
} from './nodeStyles';

const StartNodeComponent: React.FC<NodeProps> = ({
  id,
  data,
  selected,
}) => {
  // 类型断言
  const nodeData = data as unknown as StartNodeData;

  // 使用统一的执行状态 Hook
  const {
    executionStatus,
    isRunning,
    isCompleted,
    isFailed,
  } = useNodeExecutionStatus(id);

  // 截断显示的任务 prompt
  const displayPrompt = nodeData.task_prompt
    ? nodeData.task_prompt.length > 50
      ? `${nodeData.task_prompt.substring(0, 50)}...`
      : nodeData.task_prompt
    : '点击配置任务';

  return (
    <div
      className={getNodeContainerClasses('start', executionStatus, selected, isRunning)}
      data-node-id={id}
      data-node-type="start"
      data-execution-status={executionStatus}
    >
      {/* 节点头部 */}
      <div className={getNodeHeaderClasses('start')}>
        {/* 播放图标 */}
        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm font-medium text-green-600">
          {nodeData.label || '开始'}
        </span>
      </div>

      {/* 节点内容 */}
      <div className="px-3 py-2">
        <div className={cn(
          'text-sm text-gray-600',
          !nodeData.task_prompt && 'text-gray-400 italic',
        )}>
          {displayPrompt}
        </div>
        {nodeData.description && (
          <div className="mt-1 text-xs text-gray-400">
            {nodeData.description}
          </div>
        )}
      </div>

      {/* 输出连接点（右侧） */}
      <OutputHandle
        isHighlighted={isRunning}
        isCompleted={isCompleted}
        isFailed={isFailed}
      />
    </div>
  );
};

StartNodeComponent.displayName = 'StartNode';

export const StartNode = memo(StartNodeComponent);
export default StartNode;
